/**
 * useNotifications — Fetches and manages in-app notifications from Supabase.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { setupRealtimeConnection } from '@/utils/realtimeUtils';
import { debugLog } from '@/utils/debug';
import { logger } from '@/utils/logger';
import { alertPing } from '@/lib/notificationSound';

// Notification types that should trigger sound + vibration (task-oriented)
const HIGH_ATTENTION_TYPES = new Set([
  'reward_pledge_received',
  'reward_pledge_accepted',
  'proof_submitted',
  'proof_approved',
  'proof_rejected',
  'pledge_goal_completed',
  'pledge_fulfilled',
  'goal_shared',
  'task_assigned',
  'task_accepted',
  'connection_request',
]);

// Custom event name for external refresh triggers (e.g., after AI suggestions cleanup)
const NOTIFICATIONS_REFRESH_EVENT = 'notifications:refresh';

interface Notification {
  id: string;
  type: string;
  content: any;
  is_read: boolean;
  created_at: string;
  [key: string]: any;
}

export const useNotifications = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [realtimeStatus, setRealtimeStatus] = useState<'connected' | 'disconnected' | 'error'>('disconnected');
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null);
  const unsubscribeRef = useRef<() => void | null>(null);
  const retryCountRef = useRef<number>(0);
  const maxRetries = 3;

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    
    debugLog(`🔔 Fetching notifications for user: ${user.id}`);
    setLoading(true);
    
    try {
      const [{ data, error }, unread] = await Promise.all([
        supabase
          .from('notifications')
          .select('id, user_id, type, content, is_read, created_at')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(100),
        supabase
          .from('notifications')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('is_read', false),
      ]);

      if (error) throw error;

      // Process notifications - parse JSON content if needed
      const processedNotifications = (data || []).map(notification => ({
        ...notification,
        content: typeof notification.content === 'string' 
          ? JSON.parse(notification.content) 
          : notification.content
      }));
      
      debugLog(`✅ Fetched ${processedNotifications.length} notifications, ${processedNotifications.filter(n => !n.is_read).length} unread`);
      
      setNotifications(processedNotifications);
      setUnreadCount(unread.count ?? processedNotifications.filter(n => !n.is_read).length);
      setLastUpdateTime(new Date());
    } catch (error) {
      logger.error('❌ Error fetching notifications:', error);
      // Don't show destructive toast for fetch errors - they're often transient
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  const setupRealtimeSubscription = useCallback(async () => {
    if (!user) return;
    
    // Clean up any existing subscription first
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    
    debugLog(`Setting up realtime subscription for notifications for user: ${user.id}`);
    
    try {
      // Set up realtime subscription directly (no need to call edge function first)
      const channelName = `notifications:${user.id}`;
      
      const { unsubscribe } = await setupRealtimeConnection(
        supabase,
        channelName,
        user.id,
        {
          onConnect: () => {
            setRealtimeStatus('connected');
            retryCountRef.current = 0;
          },
          onDisconnect: () => {
            setRealtimeStatus('disconnected');
          },
          onError: () => {
            // Don't set error status for transient issues - notifications still work via polling
            // Only set error after all retries exhausted
            if (retryCountRef.current >= maxRetries) {
              setRealtimeStatus('disconnected');
            }
          },
          onNotification: (payload: unknown) => {
            const p = payload as { eventType?: string; new?: Record<string, unknown> };
            logger.debug('Real-time notification update:', p.eventType);
            
            // Show toast notification for new notifications
            if (p.eventType === 'INSERT' && p.new) {
              const notification = p.new as Notification;
              if (!notification.is_read) {
                // Show different toasts based on notification type
                let title = "New Notification";
                let description = getNotificationDescription(notification);
                
                if (notification.type === 'proof_submitted') {
                  title = "🔍 Proof Submitted";
                  description = "Someone submitted proof for a reward you offered!";
                } else if (notification.type === 'proof_approved') {
                  title = "🎉 Proof Approved!";
                  description = "Your proof was approved! Well done!";
                } else if (notification.type === 'proof_rejected') {
                  title = "⚠️ Proof Needs Work";
                  description = "Your proof was rejected. Please check the feedback.";
                } else if (notification.type === 'reward_pledge_received') {
                  title = "🎁 New Reward Offer!";
                  description = "Someone offered you a reward!";
                } else if (notification.type === 'reward_pledge_accepted') {
                  title = "✅ Reward Accepted!";
                  description = "Your reward offer was accepted!";
                } else if (notification.type === 'pledge_goal_completed') {
                  title = "🏆 Goal Completed!";
                  description = "A pledged goal has been completed! Review and fulfill the reward.";
                } else if (notification.type === 'pledge_fulfilled') {
                  title = "🎉 Reward Fulfilled!";
                  description = "Your reward has been fulfilled! Congratulations!";
                } else if (notification.type === 'category_suggestions') {
                  title = "✨ AI Category Suggestions";
                  description = "We have category suggestions for your goals!";
                }
                
                // Show toast notification
                toast({
                  title,
                  description,
                  duration: 6000,
                });

                // Play chime + vibrate for high-attention (task-style) notifications
                if (HIGH_ATTENTION_TYPES.has(notification.type)) {
                  alertPing();
                }
              }
            }
            
            // Refresh notifications to get the latest data
            fetchNotifications();
          }
        }
      );
      
      unsubscribeRef.current = unsubscribe;
      
    } catch (error) {
      logger.error('Error setting up realtime subscription:', error);
      setRealtimeStatus('error');
    }
  }, [user, fetchNotifications]);

  // Connect to realtime and fetch initial notifications
  useEffect(() => {
    if (user) {
      fetchNotifications();
      setupRealtimeSubscription();
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
    
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [user, fetchNotifications, setupRealtimeSubscription]);

  // Listen for external refresh events (e.g., after AI suggestions cleanup deletes notifications)
  useEffect(() => {
    const handleRefresh = () => {
      debugLog('🔔 External refresh event received, fetching notifications');
      fetchNotifications();
    };

    window.addEventListener(NOTIFICATIONS_REFRESH_EVENT, handleRefresh);
    return () => {
      window.removeEventListener(NOTIFICATIONS_REFRESH_EVENT, handleRefresh);
    };
  }, [fetchNotifications]);

  // Notification operations
  const markAsRead = useCallback(async (notificationId: string) => {
    if (!user) return;
    
    debugLog('🔸 Marking notification as read:', notificationId);
    
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId)
        .eq('user_id', user.id);
        
      if (error) throw error;
      
      debugLog('✅ Successfully marked notification as read');
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      logger.error('❌ Error marking notification as read:', error);
      toast({
        title: 'Error',
        description: 'Failed to mark notification as read',
        variant: 'destructive',
      });
    }
  }, [user, toast]);

  const markAllAsRead = useCallback(async () => {
    if (!user || !notifications.length) return;
    
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);
        
      if (error) throw error;
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => ({ ...n, is_read: true }))
      );
      
      setUnreadCount(0);
    } catch (error) {
      logger.error('Error marking all notifications as read:', error);
      toast({
        title: 'Error',
        description: 'Failed to mark all notifications as read',
        variant: 'destructive',
      });
    }
  }, [user, notifications, toast]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId)
        .eq('user_id', user.id);
        
      if (error) throw error;
      
      // Update local state
      const removedNotification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      if (removedNotification && !removedNotification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      logger.error('Error deleting notification:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete notification',
        variant: 'destructive',
      });
    }
  }, [user, notifications, toast]);

  const reconnect = useCallback(() => {
    retryCountRef.current = 0; // Reset retry counter
    setupRealtimeSubscription();
    fetchNotifications();
  }, [setupRealtimeSubscription, fetchNotifications]);

  // Helper to get a readable description for each notification type
  const getNotificationDescription = useCallback((notification: Notification) => {
    try {
      if (notification.type === 'connection_request') {
        let content = notification.content;
        if (typeof content === 'string') {
          content = JSON.parse(content);
        }
        
        return `${content.sender_name || 'Someone'} sent you a connection request`;
      } else if (notification.type === 'connection_response') {
        let content = notification.content;
        if (typeof content === 'string') {
          content = JSON.parse(content);
        }
        
        if (content.status === 'accepted') {
          return `${content.receiver_name || 'Someone'} accepted your connection request`;
        } else {
          return `${content.receiver_name || 'Someone'} declined your connection request`;
        }
      } else if (notification.type === 'goal_shared') {
        let content = notification.content;
        if (typeof content === 'string') {
          content = JSON.parse(content);
        }
        
        return `${content.sharer_name || 'Someone'} shared a goal with you: ${content.goal_title || 'Untitled Goal'}`;
      } else if (notification.type === 'reward_pledge_received') {
        return `You received a reward pledge! Check your rewards to accept it.`;
      } else if (notification.type === 'reward_pledge_accepted') {
        return `Your reward pledge was accepted! The recipient is ready to work on their goal.`;
      } else if (notification.type === 'proof_submitted') {
        return `A proof has been submitted for your review! Check your rewards to approve or reject it.`;
      } else if (notification.type === 'proof_approved') {
        return `Your proof was approved! The reward has been fulfilled.`;
      } else if (notification.type === 'proof_rejected') {
        let content = notification.content;
        if (typeof content === 'string') {
          content = JSON.parse(content);
        }
        return `Your proof was rejected${content.feedback ? `: ${content.feedback}` : '. Please resubmit with better evidence.'}`;
      } else if (notification.type === 'pledge_goal_completed') {
        let content = notification.content;
        if (typeof content === 'string') content = JSON.parse(content);
        return `${content.recipient_name || 'Someone'} completed "${content.goal_title || 'a goal'}"! Review and fulfill the reward.`;
      } else if (notification.type === 'pledge_fulfilled') {
        let content = notification.content;
        if (typeof content === 'string') content = JSON.parse(content);
        return `Your reward for "${content.goal_title || 'your goal'}" has been fulfilled by ${content.giver_name || 'someone'}! 🎉`;
      } else if (notification.type === 'category_suggestions') {
        let content = notification.content;
        if (typeof content === 'string') {
          content = JSON.parse(content);
        }
        const count = content.suggestions?.length || 1;
        return `AI has ${count} category suggestion${count > 1 ? 's' : ''} for your goals`;
      } else if (notification.type === 'place_claim_submitted') {
        let content = notification.content;
        if (typeof content === 'string') content = JSON.parse(content);
        return `New business claim: ${content.place_name || content.name || 'a place'} — review in admin.`;
      } else if (notification.type === 'creator_verified') {
        return `You're a verified creator! Open your Creator hub to add posts to trails.`;
      } else if (notification.type === 'tastemaker_contribution_submitted') {
        let content = notification.content;
        if (typeof content === 'string') content = JSON.parse(content);
        return `New tastemaker pick from ${content.display_name || 'a member'} — review in admin.`;
      }

      
      return 'New notification';
    } catch (error) {
      logger.error('Error parsing notification content:', error);
      return 'New notification';
    }
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    realtimeStatus,
    lastUpdateTime,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    fetchNotifications,
    getNotificationDescription,
    reconnect
  };
};
